import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, LogIn, UserPlus } from "lucide-react";
import { PageHero } from "../components/shared/Primitives";
import { useHandoverSession } from "../hooks/useHandoverSession";

const CARE_NEEDS_PATH = "/family/care-needs";
const FAMILY_ENTITLEMENTS = ["family_free", "family_circle"];

/**
 * Entry point for the site-wide "Post My Care Needs" navigation action.
 * A thin router in front of the EXISTING native Care Needs form
 * (/family/care-needs) — it does not render a form and does not touch
 * that route's permissions (still enforced there by HandoverGate).
 *
 * Branches on the visitor's handover session:
 *   - Already signed in with a family entitlement -> straight through
 *     (Navbar already links directly here in that case too, but this
 *     covers anyone who opens this URL directly).
 *   - Signed in without a family entitlement (carer-only) -> explains
 *     why the action isn't part of their account.
 *   - Logged out -> offered BOTH signing in (existing member) and
 *     joining Family Starter free — either way landing back on the
 *     Care Needs form once complete, via the existing ?next= handover
 *     redirect mechanism (MagicLinkRequest / JoinFree persist it,
 *     MagicLinkChecking honours it after redeem).
 */
export default function CareNeedsEntry() {
  const { ready, active, entitlements } = useHandoverSession();
  const familyEntitled = ready && active && FAMILY_ENTITLEMENTS.some((e) => entitlements.includes(e));

  useEffect(() => {
    if (familyEntitled) {
      window.location.replace(CARE_NEEDS_PATH);
    }
  }, [familyEntitled]);

  if (!ready || familyEntitled) {
    return (
      <div className="container-jcc py-24 text-center text-ink-muted" data-testid="care-needs-entry-loading">
        Loading…
      </div>
    );
  }

  if (active) {
    // Signed in via the handover session, but no family entitlement —
    // per spec, carer-only members should not be shown this action
    // (Navbar already hides the link; this covers direct URL access).
    return (
      <div data-testid="care-needs-entry-not-family">
        <PageHero
          eyebrow="Post My Care Needs"
          title="This action is for families arranging care."
          description="Posting a Care Need is part of Family Starter and Family Care Office membership. Your current account doesn't include family access yet."
          accent="teal"
          bgTint="family"
        >
          <Link to="/join/family-starter" className="btn-teal" data-testid="care-needs-entry-join-family">
            Join Family Starter — free <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/" className="btn-outline" data-testid="care-needs-entry-back-home">
            Back to homepage
          </Link>
        </PageHero>
      </div>
    );
  }

  return (
    <div data-testid="care-needs-entry-logged-out">
      <PageHero
        eyebrow="Post My Care Needs"
        title="Tell us what care you're arranging — in your own words."
        description="Posting a Care Need is free for Family Starter and Family Care Office members. Sign in if you're already a member, or join Family Starter free — either way you'll land straight back here to post your Care Need."
        accent="teal"
        bgTint="family"
      >
        <Link
          to={`/auth/handover/request?next=${encodeURIComponent(CARE_NEEDS_PATH)}`}
          className="btn-outline"
          data-testid="care-needs-entry-signin"
        >
          <LogIn className="w-4 h-4" /> Sign in
        </Link>
        <Link
          to={`/join/family-starter?next=${encodeURIComponent(CARE_NEEDS_PATH)}`}
          className="btn-teal"
          data-testid="care-needs-entry-join"
        >
          <UserPlus className="w-4 h-4" /> Join Family Starter — free
        </Link>
      </PageHero>

      <section className="section pt-0">
        <div className="container-jcc max-w-3xl">
          <div className="card-base p-6 flex items-start gap-3">
            <HeartHandshake className="w-6 h-6 text-teal shrink-0 mt-0.5" strokeWidth={1.75} />
            <p className="text-sm text-ink-muted leading-relaxed">
              No card required for Family Starter. Once you sign in or join, you&rsquo;ll be
              taken straight to the Care Needs form.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
